import * as React from 'react';
import { toast } from 'react-toastify';
import { useObservable } from 'react-use-observable';

import { Button, Modal, Grid } from 'antd';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAddressKitInfo } from 'v2/hooks/useAddressKitInfo';
import type { AddressFormKit } from 'v2/interfaces/addressKit';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import kitService from 'v2/services/kit';

import AddressToDeliveryKit from './AddressToDeliveryKit';
import ConfirmAddress from './ConfirmSameAddress';
import ConfirmAddressToKits from './UpdateAddressForm';

const { useBreakpoint } = Grid;

const ModalBelt = () => {
  const screens = useBreakpoint();
  const isMobile = !screens.sm;
  const [isOpen, setIsOpen] = React.useState(false);
  const [isOpenChangeAddress, setIsOpenChangeAddress] = React.useState(false);
  const [isOpenConfirmAddress, setIsOpenConfirmAddress] = React.useState(false);
  const queryClient = useQueryClient();

  const [user] = useObservable(() => eduzzAccountsService.getUser(), []);
  const [originalUser] = useObservable(() => eduzzAccountsService.getOriginalUser(), []);
  const isNotAccessControl = !!user && !!originalUser && user.id === originalUser.id;

  const { isLoading, isError, data, isFetching } = useAddressKitInfo(isNotAccessControl);

  const onSaveNewAddressToKit = (data: AddressFormKit) => {
    const formattedData = {
      ...data,
      number: data.number || null
    };

    return kitService.saveNewAddressToKit(formattedData);
  };

  const saveNewAddressToKit = useMutation({
    mutationFn: onSaveNewAddressToKit,
    onSuccess: () => queryClient.removeQueries({ queryKey: ['addressKitInfo'] })
  });

  React.useEffect(() => {
    const hasData = !!data && !data.answeredAt && !!user && !!originalUser;
    const isReady = !isLoading && !isError && !isFetching;
    setIsOpen(hasData && isReady && isNotAccessControl);
  }, [data, isLoading, isError, isFetching, user, originalUser, isNotAccessControl]);

  const handleChangeAddress = () => {
    setIsOpen(false);
    setIsOpenChangeAddress(true);
  };

  const handleConfirmAddress = async () => {
    try {
      await saveNewAddressToKit.mutateAsync(data.address);
      setIsOpen(false);
      setIsOpenConfirmAddress(true);
    } catch (error) {
      console.error(error);
      toast.error(error);
    }
  };

  const handleClose = () => {
    if (!saveNewAddressToKit.isPending) setIsOpen(false);
  };

  if (isLoading || isError || !data) return null;

  return (
    <>
      <Modal
        open={isOpen}
        onCancel={handleClose}
        width={isMobile ? '100vw' : 900}
        title='⭐️ Parabéns pela nova conquista!'
        closable={false}
        footer={
          <div className='flex justify-end gap-2.5'>
            {data.address && (
              <Button onClick={handleConfirmAddress} size='large' disabled={saveNewAddressToKit.isPending}>
                Meu endereço está correto!
              </Button>
            )}
            <Button type='primary' onClick={handleChangeAddress} size='large' disabled={saveNewAddressToKit.isPending}>
              {data.address ? 'Atualizar endereço de entrega' : 'Cadastrar endereço de entrega'}
            </Button>
          </div>
        }
      >
        <div className='flex flex-col gap-3'>
          <p className='text-text m-0! text-base font-bold'>Olá {data.name},</p>
          <p className='text-text-secondary m-0!'>Você alcançou o {data.belt}! 🎉🎁🎊</p>
          <p className='text-text-secondary m-0!'>
            Sua jornada no 🏆 Programa Elite está evoluindo e queremos celebrar este marco com você.
          </p>
        </div>
        <p className='text-text-secondary m-0!'>
          <span className='font-bold'>Confirme seu endereço</span> para garantir a entrega do seu reconhecimento Elite.
        </p>
        <div className='mt-4 rounded-sm bg-[#f5f5f5] p-4'>
          <p className='text-text m-0! font-bold'>Endereço atual:</p>
          {!data.address ? (
            <p className='text-text m-0!'>Nenhum endereço cadastrado.</p>
          ) : (
            <AddressToDeliveryKit address={data.address} />
          )}
        </div>
      </Modal>

      <ConfirmAddressToKits isOpen={isOpenChangeAddress} setIsOpen={setIsOpenChangeAddress} data={data} />
      <ConfirmAddress isOpen={isOpenConfirmAddress} setIsOpen={setIsOpenConfirmAddress} data={data} />
    </>
  );
};

export default ModalBelt;
